import { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
// import { toast } from 'react-hot-toast';
import Button from '../components/Button';
import Checkbox from '../components/Checkbox';
import BasicInput from '../components/BasicInput';
import PasswordInput from '../components/PasswordInput';
import SocialLoginButtons from '../components/SocialLoginButtons';
import * as auth from '../api/auth';
import useAuthStore from '../stores/authStore';
import { useTranslation } from 'react-i18next';
import { isRecaptchaError } from '../utils/recaptcha';
import { trackEvent } from '@/utils/googleAnalytics';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await auth.login(email, password);
      setAccessToken(response.accessToken);

      trackEvent('user_logged_in', {
        dondon_uid: response.userId,
      });

      // This will trigger a refetch of the user profile
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      // toast.success('Login successful!');
      // console.log('Login successful!');

      // Determine where to redirect after login
      const redirectTo = getRedirectPath();
      navigate(redirectTo);
    } catch (error) {
      console.error('Login failed:', error);

      // Handle different types of errors
      if (error.response?.status === 401) {
        setError(t('auth.login.errors.invalid_credentials'));
      } else if (isRecaptchaError(error)) {
        setError(t('auth.login.errors.recaptcha_failed'));
      } else {
        setError(t('auth.login.errors.general'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to determine redirect path after login
  const getRedirectPath = () => {
    // Check for redirect parameter in URL
    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      try {
        // Decode and validate the redirect URL
        const decodedPath = decodeURIComponent(redirectParam);
        // Only allow internal paths (starting with /)
        if (decodedPath.startsWith('/') && !decodedPath.startsWith('//')) {
          return decodedPath;
        }
      } catch (error) {
        console.warn('Invalid redirect parameter:', redirectParam);
      }
    }

    // Check for state from ProtectedRoute
    const from = location.state?.from?.pathname;
    if (from && from !== '/login') {
      // Include search params if they exist
      const search = location.state?.from?.search || '';
      return from + search;
    }

    // Default to homepage
    return '/';
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');
  };
  
  return (
    <div className="container py-8">
      <div className="max-w-[406px] mx-auto">
        <div className="text-center text-[#111111] text-2xl font-bold leading-normal mb-8">
          {t('auth.login.title')}
        </div>
        
        <form onSubmit={handleLogin}>
          <BasicInput
            title={t('auth.login.email')}
            type="email"
            placeholder={t('auth.login.email_placeholder')}
            containerClassName="w-full"
            value={email}
            onChange={handleEmailChange}
            required
            maxLength={40}
          />

          <PasswordInput
            title={t('auth.login.password')}
            placeholder={t('auth.login.password_placeholder')}
            containerClassName="w-full mt-3"
            value={password}
            onChange={handlePasswordChange}
            error={error}
            required
          />

          <div className="flex justify-between items-center mt-4">
            <label className="flex items-center gap-2">
              <Checkbox 
                checked={rememberMe} 
                onCheckedChange={setRememberMe} 
              />
              <span className="text-sm">{t('auth.login.remember_me')}</span>
            </label>
            <Link to="/forgot-password" className="text-sm underline">
              {t('auth.login.forgot_password')}
            </Link>
          </div>

          <Button 
            variant="primary" 
            size="xl" 
            className="w-full mt-8"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? t('auth.login.loading') : t('auth.login.submit')}
          </Button>

          <Button 
            variant="default" 
            size="xl" 
            className="w-full mt-4"
            as={Link}
            to="/signup"
          >
            {t('auth.login.new_user')}
          </Button>

          <SocialLoginButtons />
        </form>
      </div>
    </div>
  );
};

export default LoginPage; 